'use client'

import { PathParams, useFiltersStore } from '@/lib/store/filters-store'
import { useEffect, useMemo, useRef } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import { getFiltersForContent } from '@/lib/filters/filters'
import { getListingFilterQuery } from '@/lib/filters/listing-navigation'
import { useUserContext } from '@/lib/contexts/UserContext'
import { useSearchStore } from '@/lib/store/search-store'

/**
 * This client component is responsible for handling the router logic for the filter store.
 * It should be placed in the root layout.
 */
const FiltersStoreRouter = () => {
  const { mainCategory, category, subCategory, content } = useParams<PathParams>()

  const searchParams = useSearchParams()

  const setFilters = useFiltersStore((state) => state.setFilters)
  const setParams = useFiltersStore((state) => state.setParams)
  const initializingFilters = useRef(false)

  // Sync router params with filters store
  useEffect(() => {
    console.debug('Router params changed', {
      mainCategory,
      category,
      subCategory,
      content,
    })
    setParams({
      mainCategory,
      category,
      subCategory,
      content,
    })
  }, [category, content, mainCategory, setParams, subCategory])

  // Initialize filters store with URL params
  const { isAuthenticating, hasProFeature } = useUserContext()

  const userIsPremium = useMemo(() => {
    return hasProFeature('assets-download')
  }, [hasProFeature])

  const searchQuery = useSearchStore((state) => state.search)
  const searchMainCategorySlug = useSearchStore((state) => state.searchMainCategorySlug)

  // The Main Category slug can also come from the search query
  const mainCategorySlugFromQuery = searchParams?.get('main_category')
  const resolvedMainCategorySlug = mainCategory ?? mainCategorySlugFromQuery ?? searchMainCategorySlug ?? undefined

  useEffect(() => {
    // Wait for user authentication, as some filters depend on it
    if (isAuthenticating) {
      console.debug('User is authenticating, skipping filter initialization')
      return
    }

    const availableFilters = getFiltersForContent({
      mainCategorySlug: resolvedMainCategorySlug,
      categorySlug: category,
      subCategorySlug: subCategory,
      contentSlug: content,
      searchQuery: searchParams?.toString(),
      userIsPremium,
      query: searchQuery,
    })

    initializingFilters.current = true
    try {
      setFilters(availableFilters)
    } finally {
      initializingFilters.current = false
    }
  }, [
    category,
    content,
    isAuthenticating,
    searchQuery,
    resolvedMainCategorySlug,
    searchParams,
    setFilters,
    subCategory,
    userIsPremium,
  ])

  useEffect(
    () =>
      useFiltersStore.subscribe((state, previous) => {
        if (initializingFilters.current || state.filters === previous.filters) return
        if (!state.params.mainCategory && !window.location.pathname.startsWith('/search')) return
        const parameters = getListingFilterQuery(state.filters)
        if (parameters.toString() === getListingFilterQuery(previous.filters).toString()) return

        const url = new URL(window.location.href)
        for (const name of ['sort', 'premium']) {
          const value = parameters.get(name)
          if (value === null) url.searchParams.delete(name)
          else url.searchParams.set(name, value)
        }
        url.searchParams.delete('page')
        // Keep the current entry in sync without refetching or adding a history entry for each toggle.
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
      }),
    []
  )

  useEffect(() => {
    console.debug('FiltersStoreRouter mounted')
  }, [])

  return null
}

FiltersStoreRouter.displayName = 'FiltersStoreRouter'

export default FiltersStoreRouter
